// apps/web/src/routes/turfs/index.tsx
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { track } from '@turfchale/analytics'
import {
  type DayNightPrice,
  deriveDayNightPrice,
  supabase,
  useAuthStore,
} from '@turfchale/supabase'
import { addDays, format } from 'date-fns'
import { CalendarDays, ChevronDown, Clock, MapPin, Search, Star, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Logo } from '@/components/ui/Logo'

export const Route = createFileRoute('/turfs/')({
  component: BrowseTurfsPage,
})

type DateOption = 'today' | 'tomorrow' | { label: string; value: string }

interface DropdownOption<T> {
  label: string
  value: T
}

interface SlotInfo {
  date: string
  end_time: string
  id: string
  price: null | number
  start_time: string
  status: 'booked' | 'held' | 'open'
  turf_id: string
}

type SportType =
  | 'badminton'
  | 'basketball'
  | 'cricket'
  | 'football'
  | 'other'
  | 'pickleball'
  | 'tennis'
  | 'volleyball'

type TimeRange = 'afternoon' | 'any' | 'evening' | 'morning'

interface Turf {
  address: string
  avg_rating: number
  city: string
  cover_image_url: null | string
  id: string
  name: string
  price_per_hour: number
  slug: string
  sport_types: SportType[]
  total_reviews: number
}

const ALL_SPORTS: DropdownOption<SportType>[] = [
  { label: '⚽ Football', value: 'football' },
  { label: '🏏 Cricket', value: 'cricket' },
  { label: '🏸 Badminton', value: 'badminton' },
  { label: '🏀 Basketball', value: 'basketball' },
  { label: '🏐 Volleyball', value: 'volleyball' },
  { label: '🏓 Pickleball', value: 'pickleball' },
  { label: '🎾 Tennis', value: 'tennis' },
]

const SPORT_LABELS: Record<string, string> = {
  badminton: '🏸 Badminton',
  basketball: '🏀 Basketball',
  cricket: '🏏 Cricket',
  football: '⚽ Football',
  other: '🎯 Other',
  pickleball: '🏓 Pickleball',
  tennis: '🎾 Tennis',
  volleyball: '🏐 Volleyball',
}

const SPORT_EMOJI: Record<string, string> = {
  badminton: '🏸',
  basketball: '🏀',
  cricket: '🏏',
  football: '⚽',
  other: '🎯',
  pickleball: '🏓',
  tennis: '🎾',
  volleyball: '🏐',
}

const TIME_RANGE_OPTIONS: DropdownOption<TimeRange>[] = [
  { label: '🌅 Morning (6–12 PM)', value: 'morning' },
  { label: '☀️ Afternoon (12–5 PM)', value: 'afternoon' },
  { label: '🌙 Evening (5–11 PM)', value: 'evening' },
]

const DATE_OPTIONS: DateOption[] = [
  'today',
  'tomorrow',
  {
    label: format(addDays(new Date(), 2), 'EEE d MMM'),
    value: format(addDays(new Date(), 2), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 3), 'EEE d MMM'),
    value: format(addDays(new Date(), 3), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 4), 'EEE d MMM'),
    value: format(addDays(new Date(), 4), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 5), 'EEE d MMM'),
    value: format(addDays(new Date(), 5), 'yyyy-MM-dd'),
  },
  {
    label: format(addDays(new Date(), 6), 'EEE d MMM'),
    value: format(addDays(new Date(), 6), 'yyyy-MM-dd'),
  },
]

function BrowseTurfsPage() {
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile)

  const [search, setSearch] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [selectedSports, setSelectedSports] = useState<SportType[]>([])
  const [selectedCity, setSelectedCity] = useState<null | string>(null)
  const [selectedDate, setSelectedDate] = useState<null | string>(null)
  const [selectedTimeRanges, setSelectedTimeRanges] = useState<TimeRange[]>([])

  const [hasSearched, setHasSearched] = useState(false)

  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data: cities = [] } = useCities()

  const shouldFetch =
    hasSearched &&
    (debouncedSearch.trim().length > 0 ||
      selectedSports.length > 0 ||
      selectedCity !== null ||
      selectedDate !== null ||
      selectedTimeRanges.length > 0)

  const { data: turfs = [], isLoading } = useTurfs(
    debouncedSearch,
    selectedSports,
    selectedCity,
    shouldFetch,
  )
  const pricingByTurf = useTurfPricing(turfs, shouldFetch)

  const { data: slotsByDateTurf = new Map() } = useSlotsByDateTurf(
    turfs,
    selectedDate,
    selectedTimeRanges,
    shouldFetch,
  )

  const filteredTurfs = useMemo(() => {
    if (selectedDate === null) return turfs

    return turfs.filter((turf) => {
      const key = `${turf.id}::${selectedDate}`
      return (slotsByDateTurf.get(key) ?? []).length > 0
    })
  }, [turfs, slotsByDateTurf, selectedDate])

  const hasActiveFilters =
    debouncedSearch.trim().length > 0 ||
    selectedSports.length > 0 ||
    selectedCity !== null ||
    selectedDate !== null ||
    selectedTimeRanges.length > 0

  function triggerSearch() {
    setHasSearched(true)
    track('turf_search_performed', {
      city: selectedCity ?? undefined,
      date: selectedDate ?? undefined,
      query: search.trim() || undefined,
      sport: selectedSports.length ? selectedSports.join(',') : undefined,
    })
  }

  function handleSearchKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      triggerSearch()
    }
  }

  function clearAllFilters() {
    setSearch('')
    setDebouncedSearch('')
    setSelectedSports([])
    setSelectedCity(null)
    setSelectedDate(null)
    setSelectedTimeRanges([])
    setHasSearched(false)
    searchInputRef.current?.focus()
  }

  const showEmpty = hasSearched && !isLoading && filteredTurfs.length === 0
  const showList = hasSearched && !isLoading && filteredTurfs.length > 0

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100vh' }}>
      {/* Navbar */}
      <header
        style={{
          alignItems: 'center',
          background: 'var(--bg2)',
          borderBottom: '1px solid var(--lime-line)',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '14px 24px',
        }}
      >
        <Logo fontSize={18} />
        {profile ? (
          <button
            onClick={() => navigate({ to: '/app' })}
            style={{
              background: 'var(--bg3)',
              border: '1px solid var(--lime-line)',
              borderRadius: 8,
              color: 'var(--text)',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              padding: '8px 14px',
            }}
          >
            My Bookings
          </button>
        ) : (
          <button
            onClick={() => navigate({ search: { redirect: undefined }, to: '/auth' })}
            style={{
              background: 'var(--lime)',
              border: 'none',
              borderRadius: 8,
              color: 'var(--bg)',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              padding: '8px 16px',
            }}
          >
            Sign In
          </button>
        )}
      </header>

      {/* Filters */}
      <div
        style={{
          background: 'var(--bg2)',
          borderBottom: '1px solid var(--lime-line)',
          padding: '16px 24px',
        }}
      >
        <div style={{ margin: '0 auto', maxWidth: 720 }}>
          {/* Search bar row with Search button on the right */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  alignItems: 'center',
                  background: 'var(--bg3)',
                  border: '1px solid var(--lime-line)',
                  borderRadius: 10,
                  display: 'flex',
                  gap: 8,
                  padding: '10px 14px',
                }}
              >
                <Search size={16} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
                <input
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search by turf name, city, or address…"
                  ref={searchInputRef}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text)',
                    fontSize: 14,
                    outline: 'none',
                    width: '100%',
                  }}
                  value={search}
                />
              </div>
            </div>
            <button
              onClick={triggerSearch}
              style={{
                alignSelf: 'flex-start',
                background: search.trim() || hasActiveFilters ? 'var(--lime)' : 'var(--bg3)',
                border: 'none',
                borderRadius: 10,
                color: search.trim() || hasActiveFilters ? 'var(--bg)' : 'var(--text-dim)',
                cursor: search.trim() || hasActiveFilters ? 'pointer' : 'default',
                fontSize: 13,
                fontWeight: 700,
                padding: '10px 20px',
                whiteSpace: 'nowrap',
              }}
            >
              Search
            </button>
          </div>

          {/* OR divider */}
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: 12,
              margin: '12px 0',
            }}
          >
            <div style={{ borderTop: '1px solid var(--lime-line)', flex: 1 }} />
            <span
              style={{
                color: 'var(--text-dim)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              OR
            </span>
            <div style={{ borderTop: '1px solid var(--lime-line)', flex: 1 }} />
          </div>

          {/* Filter dropdowns row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <MultiSelectDropdown
              buttonLabel="Sports"
              icon={<Star size={12} />}
              onChange={(next) => {
                setSelectedSports(next)
                if (next.length)
                  track('turf_filter_applied', { filter: 'sport', value: next.join(',') })
              }}
              options={ALL_SPORTS}
              selected={selectedSports}
            />

            <SingleSelectDropdown
              buttonLabel="City"
              icon={<MapPin size={12} />}
              onChange={setSelectedCity}
              options={cities.map((c) => ({ label: c, value: c }))}
              selected={selectedCity}
            />

            <SingleSelectDropdown
              buttonLabel="Date"
              icon={<CalendarDays size={12} />}
              onChange={setSelectedDate}
              options={DATE_OPTIONS.map((d) => ({
                label: getDateLabel(d),
                value: getDateValue(d),
              }))}
              selected={selectedDate}
            />

            <MultiSelectDropdown
              buttonLabel="Time"
              icon={<Clock size={12} />}
              onChange={setSelectedTimeRanges}
              options={TIME_RANGE_OPTIONS}
              selected={selectedTimeRanges}
            />

            {(hasActiveFilters || hasSearched) && (
              <button
                onClick={clearAllFilters}
                style={{
                  alignItems: 'center',
                  background: 'none',
                  border: '1px solid rgba(255,100,100,0.3)',
                  borderRadius: 8,
                  color: '#ff6464',
                  cursor: 'pointer',
                  display: 'flex',
                  fontSize: 11,
                  gap: 4,
                  padding: '7px 12px',
                }}
              >
                <X size={12} />
                Clear
              </button>
            )}
          </div>

          {/* Search button also at bottom when filters are active */}
          {hasActiveFilters && (
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button
                onClick={triggerSearch}
                style={{
                  background: 'var(--lime)',
                  border: 'none',
                  borderRadius: 10,
                  color: 'var(--bg)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                  padding: '10px 28px',
                }}
              >
                Search
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results area */}
      <div style={{ margin: '0 auto', maxWidth: 720, padding: '20px 16px' }}>
        {!hasSearched && (
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              padding: '80px 24px 60px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                alignItems: 'center',
                background: 'var(--bg3)',
                borderRadius: '50%',
                display: 'flex',
                height: 72,
                justifyContent: 'center',
                width: 72,
              }}
            >
              <Search size={28} style={{ color: 'var(--text-dim)' }} />
            </div>
            <div>
              <div
                style={{
                  color: 'var(--text)',
                  fontFamily: 'Clash Display',
                  fontSize: 18,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                Find your turf
              </div>
              <div
                style={{
                  color: 'var(--muted)',
                  fontSize: 13,
                  lineHeight: 1.6,
                  maxWidth: 340,
                }}
              >
                Search by name or address above,
                <br />
                or pick from the filters to discover
                <br />
                the perfect turf for your game.
              </div>
            </div>
            <div
              style={{
                background: 'var(--bg3)',
                borderRadius: 10,
                color: 'var(--text-dim)',
                fontSize: 12,
                marginTop: 8,
                padding: '10px 18px',
              }}
            >
              🔍 Try searching "Football" or "Mumbai"
            </div>
          </div>
        )}

        {hasSearched && isLoading && (
          <div style={{ color: 'var(--muted)', padding: 48, textAlign: 'center' }}>
            <div
              className="animate-spin"
              style={{
                border: '2px solid var(--lime-line)',
                borderRadius: '50%',
                borderTopColor: 'var(--lime)',
                height: 24,
                margin: '0 auto 12px',
                width: 24,
              }}
            />
            Finding turfs…
          </div>
        )}

        {showEmpty && (
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              padding: '80px 24px 60px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                alignItems: 'center',
                background: 'var(--bg3)',
                borderRadius: '50%',
                display: 'flex',
                height: 72,
                justifyContent: 'center',
                width: 72,
              }}
            >
              <MapPin size={28} style={{ color: 'var(--text-dim)' }} />
            </div>
            <div>
              <div
                style={{
                  color: 'var(--text)',
                  fontFamily: 'Clash Display',
                  fontSize: 17,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                No turfs found
              </div>
              <div
                style={{
                  color: 'var(--muted)',
                  fontSize: 13,
                  lineHeight: 1.6,
                  maxWidth: 300,
                }}
              >
                Nothing matches your current filters.
                <br />
                Try broadening your search.
              </div>
            </div>
            <button
              onClick={clearAllFilters}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(184,236,60,.4)')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--lime-line)')}
              style={{
                background: 'var(--bg3)',
                border: '1px solid var(--lime-line)',
                borderRadius: 10,
                color: 'var(--text)',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                marginTop: 4,
                padding: '10px 20px',
                transition: 'all 0.12s',
              }}
            >
              ← Clear all filters
            </button>
          </div>
        )}

        {showList && (
          <>
            <div style={{ display: 'grid', gap: 10 }}>
              {filteredTurfs.map((turf, index) => {
                const hasAvailableSlots =
                  selectedDate !== null
                    ? (slotsByDateTurf.get(`${turf.id}::${selectedDate}`) ?? []).length > 0
                    : null

                return (
                  <TurfCard
                    dayNight={pricingByTurf.get(turf.id)}
                    hasAvailableSlots={hasAvailableSlots}
                    key={turf.id}
                    position={index}
                    turf={turf}
                  />
                )
              })}
            </div>

            {selectedDate !== null && (
              <div
                style={{
                  color: 'var(--muted)',
                  fontSize: 11,
                  marginTop: 12,
                  textAlign: 'center',
                }}
              >
                Showing {filteredTurfs.length} turf{filteredTurfs.length > 1 ? 's' : ''} with
                available slots on selected date
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function getDateLabel(opt: DateOption): string {
  if (opt === 'today') return 'Today'
  if (opt === 'tomorrow') return 'Tomorrow'
  return opt.label
}

function getDateValue(opt: DateOption): string {
  if (opt === 'today') return format(new Date(), 'yyyy-MM-dd')
  if (opt === 'tomorrow') return format(addDays(new Date(), 1), 'yyyy-MM-dd')
  return opt.value
}

// ────────────────────────────────────────────────────────────
// Generic multiselect dropdown component
// ────────────────────────────────────────────────────────────
function MultiSelectDropdown<T extends string>({
  buttonLabel,
  icon,
  onChange,
  options,
  selected,
}: {
  buttonLabel: string
  icon: React.ReactNode
  onChange: (selected: T[]) => void
  options: DropdownOption<T>[]
  selected: T[]
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const displayLabel = selected.length === 0 ? buttonLabel : `${buttonLabel} (${selected.length})`

  function toggle(val: T) {
    if (selected.includes(val)) {
      onChange(selected.filter((s) => s !== val))
    } else {
      onChange([...selected, val])
    }
  }

  return (
    <div ref={ref} style={{ flexShrink: 0, position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          alignItems: 'center',
          background: selected.length > 0 ? 'var(--lime)' : 'var(--bg3)',
          border: '1px solid var(--lime-line)',
          borderRadius: 8,
          color: selected.length > 0 ? 'var(--bg)' : 'var(--text)',
          cursor: 'pointer',
          display: 'flex',
          fontSize: 12,
          fontWeight: selected.length > 0 ? 700 : 500,
          gap: 6,
          padding: '7px 12px',
          whiteSpace: 'nowrap',
        }}
      >
        {selected.length > 0 && icon}
        {displayLabel}
        {selected.length > 0 ? (
          <X
            onClick={(e) => {
              e.stopPropagation()
              onChange([])
            }}
            size={12}
          />
        ) : (
          <ChevronDown size={12} />
        )}
      </button>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              bottom: 0,
              left: 0,
              position: 'fixed',
              right: 0,
              top: 0,
              zIndex: 9,
            }}
          />
          <div
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--lime-line)',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              left: 0,
              maxHeight: 280,
              minWidth: 200,
              overflowY: 'auto',
              padding: '6px',
              position: 'absolute',
              top: 'calc(100% + 4px)',
              zIndex: 10,
            }}
          >
            {options.map((opt) => {
              const isChecked = selected.includes(opt.value)
              return (
                <label
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg3)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  style={{
                    alignItems: 'center',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    fontSize: 12,
                    gap: 8,
                    padding: '8px 10px',
                    transition: 'background 0.1s',
                  }}
                >
                  <div
                    style={{
                      alignItems: 'center',
                      border: `2px solid ${isChecked ? 'var(--lime)' : 'var(--lime-line)'}`,
                      borderRadius: 4,
                      display: 'flex',
                      height: 16,
                      justifyContent: 'center',
                      width: 16,
                    }}
                  >
                    {isChecked && (
                      <span style={{ color: 'var(--lime)', fontSize: 10, lineHeight: 1 }}>✓</span>
                    )}
                  </div>
                  <span>{opt.label}</span>
                </label>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function PriceTier({ label, price }: { label: string; price: number }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div
        style={{
          color: 'var(--muted)',
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          alignItems: 'baseline',
          display: 'flex',
          fontFamily: 'Clash Display',
          fontSize: 15,
          fontWeight: 700,
          gap: 1,
          justifyContent: 'flex-end',
        }}
      >
        ₹{price.toLocaleString('en-IN')}
        <span style={{ color: 'var(--muted)', fontSize: 10, fontWeight: 400 }}>/hr</span>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Generic single-select dropdown component
// ────────────────────────────────────────────────────────────
function SingleSelectDropdown<T extends string>({
  buttonLabel,
  icon,
  onChange,
  options,
  selected,
}: {
  buttonLabel: string
  icon: React.ReactNode
  onChange: (selected: null | T) => void
  options: DropdownOption<T>[]
  selected: null | T
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const hasSelection = selected !== null
  const selectedLabel = options.find((o) => o.value === selected)?.label
  const displayLabel = hasSelection ? `${buttonLabel}: ${selectedLabel}` : buttonLabel

  function pick(val: T) {
    onChange(val === selected ? null : val)
    setOpen(false)
  }

  return (
    <div ref={ref} style={{ flexShrink: 0, position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          alignItems: 'center',
          background: hasSelection ? 'var(--lime)' : 'var(--bg3)',
          border: '1px solid var(--lime-line)',
          borderRadius: 8,
          color: hasSelection ? 'var(--bg)' : 'var(--text)',
          cursor: 'pointer',
          display: 'flex',
          fontSize: 12,
          fontWeight: hasSelection ? 700 : 500,
          gap: 6,
          padding: '7px 12px',
          whiteSpace: 'nowrap',
        }}
      >
        {hasSelection && icon}
        {displayLabel}
        {hasSelection ? (
          <X
            onClick={(e) => {
              e.stopPropagation()
              onChange(null)
            }}
            size={12}
          />
        ) : (
          <ChevronDown size={12} />
        )}
      </button>

      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              bottom: 0,
              left: 0,
              position: 'fixed',
              right: 0,
              top: 0,
              zIndex: 9,
            }}
          />
          <div
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--lime-line)',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              left: 0,
              maxHeight: 280,
              minWidth: 200,
              overflowY: 'auto',
              padding: '6px',
              position: 'absolute',
              top: 'calc(100% + 4px)',
              zIndex: 10,
            }}
          >
            {options.map((opt) => {
              const isChecked = opt.value === selected
              return (
                <label
                  key={opt.value}
                  onClick={() => pick(opt.value)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg3)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  style={{
                    alignItems: 'center',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    fontSize: 12,
                    gap: 8,
                    padding: '8px 10px',
                    transition: 'background 0.1s',
                  }}
                >
                  <div
                    style={{
                      alignItems: 'center',
                      border: `2px solid ${isChecked ? 'var(--lime)' : 'var(--lime-line)'}`,
                      borderRadius: '50%',
                      display: 'flex',
                      height: 16,
                      justifyContent: 'center',
                      width: 16,
                    }}
                  >
                    {isChecked && (
                      <span
                        style={{
                          background: 'var(--lime)',
                          borderRadius: '50%',
                          height: 8,
                          width: 8,
                        }}
                      />
                    )}
                  </div>
                  <span>{opt.label}</span>
                </label>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function TurfCard({
  dayNight,
  hasAvailableSlots,
  position,
  turf,
}: {
  dayNight?: DayNightPrice
  hasAvailableSlots: boolean | null
  position: number
  turf: Turf
}) {
  const firstSport = turf.sport_types?.[0]

  return (
    <Link
      onClick={() =>
        track('turf_card_clicked', { position, turf_id: turf.id, turf_name: turf.name })
      }
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(184,236,60,.4)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--lime-line)')}
      params={{ turfSlug: turf.slug }}
      style={{
        alignItems: 'center',
        background: 'var(--bg2)',
        border: '1px solid var(--lime-line)',
        borderRadius: 12,
        cursor: 'pointer',
        display: 'flex',
        gap: 14,
        padding: 14,
        textDecoration: 'none',
        transition: 'border-color 0.15s',
      }}
      to="/turfs/$turfSlug"
    >
      <div
        style={{
          background: 'var(--bg3)',
          borderRadius: 8,
          flexShrink: 0,
          height: 72,
          overflow: 'hidden',
          width: 72,
        }}
      >
        {turf.cover_image_url ? (
          <img
            alt={turf.name}
            src={turf.cover_image_url}
            style={{ height: '100%', objectFit: 'cover', width: '100%' }}
          />
        ) : (
          <div
            style={{
              alignItems: 'center',
              color: 'var(--text-dim)',
              display: 'flex',
              fontSize: 28,
              height: '100%',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {firstSport ? (SPORT_EMOJI[firstSport] ?? '📍') : '📍'}
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'Clash Display',
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {turf.name}
        </div>
        <div
          style={{
            alignItems: 'center',
            color: 'var(--muted)',
            display: 'flex',
            fontSize: 12,
            gap: 4,
            marginBottom: 6,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <MapPin size={12} style={{ flexShrink: 0 }} />
          {turf.address}
          {turf.city && <span style={{ color: 'var(--text-dim)' }}>· {turf.city}</span>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {turf.sport_types?.slice(0, 3).map((s) => (
            <span
              key={s}
              style={{
                background: 'var(--lime-dim)',
                borderRadius: 4,
                color: 'var(--lime)',
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 6px',
              }}
            >
              {SPORT_LABELS[s] ?? s}
            </span>
          ))}
          {hasAvailableSlots === true && (
            <span
              style={{
                background: 'rgba(184,236,60,0.15)',
                borderRadius: 4,
                color: 'var(--lime)',
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 6px',
              }}
            >
              ✅ Available on selected date
            </span>
          )}
        </div>
        <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
          {turf.total_reviews > 0 ? (
            <div style={{ alignItems: 'center', display: 'flex', gap: 4 }}>
              <Star size={12} style={{ color: 'var(--lime)', fill: 'var(--lime)' }} />
              <span style={{ color: 'var(--lime)', fontSize: 12, fontWeight: 600 }}>
                {turf.avg_rating.toFixed(1)}
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 11 }}>({turf.total_reviews})</span>
            </div>
          ) : (
            <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>No reviews yet</span>
          )}
          {dayNight && !dayNight.flat ? (
            <div style={{ display: 'flex', gap: 14 }}>
              <PriceTier label="Day" price={dayNight.day} />
              <PriceTier label="Night" price={dayNight.night} />
            </div>
          ) : (
            <div
              style={{
                alignItems: 'baseline',
                display: 'flex',
                fontFamily: 'Clash Display',
                fontSize: 16,
                fontWeight: 700,
                gap: 1,
              }}
            >
              ₹{(dayNight?.day ?? turf.price_per_hour).toLocaleString('en-IN')}
              <span style={{ color: 'var(--muted)', fontSize: 11, fontWeight: 400 }}>/hr</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}

// ────────────────────────────────────────────────────────────
// Data fetching hooks
// ────────────────────────────────────────────────────────────

function useCities() {
  return useQuery({
    queryFn: async () => {
      const { data, error } = await supabase
        .from('turfs')
        .select('city')
        .eq('status', 'active')
        .order('city')

      if (error) throw error

      const seen = new Set<string>()
      return (data as { city: string }[]).reduce<string[]>((acc, row) => {
        if (!seen.has(row.city)) {
          seen.add(row.city)
          acc.push(row.city)
        }
        return acc
      }, [])
    },
    queryKey: ['turf-cities'],
    staleTime: 5 * 60 * 1000,
  })
}

function useSlotsByDateTurf(
  turfs: Turf[],
  date: null | string,
  timeRanges: TimeRange[],
  enabled: boolean,
) {
  const turfIds = turfs.map((t) => t.id)

  return useQuery({
    enabled: enabled && turfIds.length > 0 && date !== null,
    queryFn: async () => {
      let query = supabase
        .from('slots')
        .select('turf_id, date, id, start_time, end_time, status')
        .in('turf_id', turfIds)
        .eq('date', date!)
        .in('status', ['open'])

      if (timeRanges.length > 0) {
        const conditions: { gte: string; lt: string }[] = []
        for (const range of timeRanges) {
          if (range === 'morning') conditions.push({ gte: '06:00:00', lt: '12:00:00' })
          if (range === 'afternoon') conditions.push({ gte: '12:00:00', lt: '17:00:00' })
          if (range === 'evening') conditions.push({ gte: '17:00:00', lt: '23:00:00' })
        }
        if (conditions.length > 0) {
          const orParts = conditions.map(
            (c) => `and(start_time.gte.${c.gte},start_time.lt.${c.lt})`,
          )
          query = query.or(orParts.join(','))
        }
      }

      query = query.order('start_time')

      const { data, error } = await query
      if (error) throw error

      const rows = data as Pick<
        SlotInfo,
        'date' | 'end_time' | 'id' | 'start_time' | 'status' | 'turf_id'
      >[]

      const map = new Map<string, typeof rows>()
      for (const row of rows) {
        const key = `${row.turf_id}::${row.date}`
        const list = map.get(key) ?? []
        list.push(row)
        map.set(key, list)
      }
      return map
    },
    queryKey: ['browse-slots', [...turfIds].sort(), date, [...timeRanges].sort()],
  })
}

function useTurfPricing(turfs: Turf[], enabled: boolean): Map<string, DayNightPrice> {
  const ids = turfs.map((t) => t.id)

  const { data: rows = [] } = useQuery({
    enabled: enabled && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('turf_pricing_schedule')
        .select('turf_id, hour, price')
        .in('turf_id', ids)
      if (error) throw error
      return data as { hour: number; price: number; turf_id: string }[]
    },
    queryKey: ['turf-pricing', [...ids].sort()],
  })

  return useMemo(() => {
    const byTurf = new Map<string, { hour: number; price: number }[]>()
    for (const row of rows) {
      const list = byTurf.get(row.turf_id) ?? []
      list.push({ hour: row.hour, price: row.price })
      byTurf.set(row.turf_id, list)
    }
    const result = new Map<string, DayNightPrice>()
    for (const turf of turfs) {
      result.set(turf.id, deriveDayNightPrice(byTurf.get(turf.id) ?? [], turf.price_per_hour))
    }
    return result
  }, [rows, turfs])
}

function useTurfs(search: string, sports: SportType[], city: null | string, enabled: boolean) {
  return useQuery({
    enabled,
    queryFn: async () => {
      let query = supabase
        .from('turfs')
        .select(
          'id, slug, name, address, city, cover_image_url, price_per_hour, sport_types, avg_rating, total_reviews',
        )
        .eq('status', 'active')
        .order('avg_rating', { ascending: false })

      const safeTerm = search.trim().replace(/[%_,()]/g, '')
      if (safeTerm) {
        const term = `%${safeTerm}%`
        query = query.or(`name.ilike.${term},city.ilike.${term},address.ilike.${term}`)
      }

      if (sports.length > 0) {
        const orParts = sports.map((s) => `sport_types.cs.{${s}}`)
        query = query.or(orParts.join(','))
      }

      if (city) {
        query = query.eq('city', city)
      }

      const { data, error } = await query
      if (error) throw error
      return data as Turf[]
    },
    queryKey: ['turfs', search, [...sports].sort(), city],
  })
}
